import React, { useState } from 'react';
import clsx from 'clsx';
import { XCircleIcon } from '@heroicons/react/24/outline';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import Dialog from '@dashboard/components/Dialog';
import { Form } from '@dashboard/components/Formik';
import Link from '@dashboard/components/Link';
import Tooltip from '@dashboard/components/Tooltip';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { toast } from '@dashboard/context/ToastContext';
import { submissionKey } from '@dashboard/lib/queryKeys';
import { fetcher } from '@dashboard/services/request';

const CancelSubmissionDialog = ({ submission }) => {
  const [open, setOpen] = useState(false);
  const { token } = useAuth();
  const queryClient = useQueryClient();

  const cancelSubmission = useMutation({
    mutationFn: values =>
      fetcher(`/sellers/${submission.seller}/submissions/${submission._id}/cancel`, token, {
        method: 'POST',
        body: JSON.stringify(values),
      }),
    // A failure can come after the cancel committed, so refresh either way.
    onSettled: () => queryClient.invalidateQueries({ queryKey: submissionKey(submission._id) }),
    onSuccess: () => {
      toast({ title: 'Submission canceled', type: 'success' });
      setOpen(false);
    },
    onError: error => {
      toast({
        title: 'An error occurred while canceling the submission',
        description: error?.message,
        type: 'error',
      });
    },
  });

  const canceled = submission.orderStatus === 'canceled';

  return (
    <>
      <Tooltip
        triggerClassName="w-fit"
        label={canceled ? 'This submission has been canceled' : ''}
        side="right"
      >
        <Link
          onClick={() => setOpen(true)}
          className={clsx('w-full flex gap-x-2 items-center no-underline', {
            'pointer-events-none opacity-50 cursor-not-allowed': canceled,
          })}
        >
          <>
            <XCircleIcon className="w-4 h-4" />
            Cancel submission
          </>
        </Link>
      </Tooltip>

      <Dialog
        title="Cancel submission"
        description="Canceling a submission removes the customer's access to its files. If the form allows one submission per person, they can submit again."
        cancelButton={null}
        confirmButton={null}
        size="md"
        open={open}
        onCancel={() => setOpen(false)}
      >
        {() => (
          <Form
            showState={false}
            initialValues={{ reason: '', sendCancellationEmail: false }}
            onSubmit={async values => {
              try {
                const data = await cancelSubmission.mutateAsync({
                  reason: values.reason,
                  sendCancellationEmail: values.sendCancellationEmail,
                });
                return { data };
              } catch (error) {
                return { error };
              }
            }}
            fields={[
              {
                name: 'reason',
                label: 'Reason for cancellation',
                tags: ['not shown to customer'],
                type: 'textarea',
              },
              {
                name: 'sendCancellationEmail',
                label: 'Send cancellation email to customer',
                type: 'checkbox',
              },
            ]}
            formSubmitAlign="end"
            submitButton={{ label: 'Cancel submission', appearance: 'primary' }}
          />
        )}
      </Dialog>
    </>
  );
};

export default CancelSubmissionDialog;
